/**
 * API Auth Bridge
 *
 * Connects the Auth0 context to the API layer.
 * This component must be rendered inside Auth0Provider to access auth context.
 *
 * When in auth mode, it registers the token getter with the API layer.
 * When in public mode, it does nothing (API calls have no auth).
 *
 * IMPORTANT: Token getter registration happens synchronously during render
 * (not in useEffect) so it is available before any child component effects
 * fire. React executes child effects before parent effects, so a useEffect-
 * based registration would always lose the race against child data fetches.
 */

import { useEffect } from "react"
import { env } from "@/lib/env"
import { useAuthAccess } from "@/lib/auth"
import { setApiTokenGetter } from "@/lib/api/authContext"

interface ApiAuthBridgeProps {
  children: React.ReactNode
}

export function ApiAuthBridge({ children }: ApiAuthBridgeProps) {
  const { getAccessToken } = useAuthAccess()

  // Register token getter synchronously during render — before child
  // component effects fire. This is a module-level variable write (not
  // React state), so it is safe to do during render.
  if (env.VITE_UI_SHELL_MODE === "auth") {
    setApiTokenGetter(getAccessToken)
  } else {
    setApiTokenGetter(null)
  }

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      setApiTokenGetter(null)
    }
  }, [])

  return <>{children}</>
}
